import React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useTranslation } from "react-i18next";
import CreditData from "./credit-data";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {Genome} from "./genome";
import WorkingInformation from "./working-information";
import Credits from "./credits";
import Academics from "./academics";
import Properties from "./properties";
import Locations from "./locations";
import BasicInformationSri from "../dashboard-ruc/BasicInformationSri";
import {Person} from "../../services/interface/query/person";
import Debts from "./debts";
import LegalInformation from "./legal-information";
import Checklists from "./checklists";
import {useFindTaxPayer} from "../../services/apis/apiTaxPayer";
import {Spinner} from "@material-tailwind/react";

export interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

export function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            className={'w-full sm:min-w-[76%] md:min-w-[80%] lg:min-w-[85%] p-4'}
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            {...other}
        >
            {value === index && (<>{children}</>)}
        </div>
    );
}

export function a11yProps(index: number) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

interface Props {
    setOpenCollapse: React.Dispatch<React.SetStateAction<boolean>>;
    person?: Person;
    loading: boolean
}

export default function VerticalTabs({setOpenCollapse, person, loading}: Props) {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { t } = useTranslation("global")
    const [value, setValue] = React.useState(0);
    const tabs = [
        {id: 0, text: 'credit-data'},
        {id: 1, text: 'genome'},
        {id: 2, text: 'sri'},
        {id: 3, text: 'working-information'},
        {id: 4, text: 'credits'},
        {id: 5, text: 'contacts'},
        {id: 6, text: 'academics'},
        {id: 7, text: 'properties'},
        {id: 8, text: 'debts'},
        {id: 9, text: 'legal-information'},
        {id: 10, text: 'checklists'},
    ]

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        setOpenCollapse(false);
    };

    const {
        mutate: getTaxPayer,
        data: successTaxPayer,
        isPending: loadingTaxPayer,
        error: errorTaxPayer,
    } = useFindTaxPayer()

    return (
        <div className={`flex ${isSmallScreen ? 'flex-col' : 'flex-row'} grow bg-white custom-card`}>
            {
                loading ?
                    <div className={'flex flex-row items-center justify-center w-full min-h-full'}>
                        <Spinner className={'h-7 w-7'}/>
                    </div> :
                    <>
                        <Tabs
                            className={'w-full sm:w-[15%] sm:min-w-[135px]'}
                            orientation={isSmallScreen ? "horizontal" : "vertical"}
                            variant="scrollable"
                            value={value}
                            onChange={handleChange}
                            sx={{
                                borderRight: !isSmallScreen ? 1 : 0, // No se aplica borde derecho si es pantalla pequeña
                                borderBottom: isSmallScreen ? 1 : 0, // Aplica borde inferior si es pantalla pequeña
                                borderColor: 'divider',
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#FF4F22', // Color de la línea de selección
                                },
                            }}
                        >
                            {tabs.map(tab => {
                                return (
                                    <Tab key={tab.id} label={t(tab.text)} {...a11yProps(tab.id)}
                                         sx={{
                                             '&.Mui-selected': {
                                                 color: '#FF4F22', // Color del texto cuando está seleccionado
                                             },
                                         }}
                                    />
                                )
                            })}
                        </Tabs>
                        <TabPanel value={value} index={0}>
                            <CreditData {...{person}}/>
                        </TabPanel>
                        <TabPanel value={value} index={1}>
                            <Genome {...{person, setValue}}/>
                        </TabPanel>
                        <TabPanel value={value} index={2}>
                            <BasicInformationSri
                                movePage={true}
                                personNatural={person}
                                {...{getTaxPayer, successTaxPayer, loadingTaxPayer, errorTaxPayer}}
                            />
                        </TabPanel>
                        <TabPanel value={value} index={3}>
                            <WorkingInformation {...{person}}/>
                        </TabPanel>
                        <TabPanel value={value} index={4}>
                            <Credits {...{person}}/>
                        </TabPanel>
                        <TabPanel value={value} index={5}>
                            <Locations {...{person}}/>
                        </TabPanel>
                        <TabPanel value={value} index={6}>
                            <Academics {...{person}}/>
                        </TabPanel>
                        <TabPanel value={value} index={7}>
                            <Properties {...{person}}/>
                        </TabPanel>
                        <TabPanel value={value} index={8}>
                            <Debts {...{person}}/>
                        </TabPanel>
                        <TabPanel value={value} index={9}>
                            <LegalInformation {...{person}}/>
                        </TabPanel>
                        <TabPanel value={value} index={10}>
                            <Checklists {...{person}}/>
                        </TabPanel>
                    </>
            }
        </div>
    );
}
